import React from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";
import { getCallbackPath, getLogoutPath } from "../api/OAuth2Client";
import { ContactDialog } from "../features/AccountContacts/ContactDialog";
import { OAuth2Callback } from "../features/Auth";
import { CallVerificationDialogContainer } from "../features/CallVerification";
import {
  BillDetails,
  Bills,
  Payments,
  Transactions,
} from "../features/Financials";
import { CreditDetails } from "../features/Financials/CreditDetails";
import { MeterPointDialog } from "../features/MeterPoints";

import { SearchContainer } from "../features/Search";
import { WrapNotesContainer } from "../features/WrapUpNotes";
import { WrapUpNotesDetails } from "../features/WrapUpNotes/WrapUpNotesDetails";
import { AccountsPage } from "./accounts/AccountsPage";
import { DashboardView } from "./accounts/Dashboard";
import { FinancialsPage } from "./accounts/FinancialsPage";
import { TicketsPage } from "./accounts/TicketsPage";

import { LoginView } from "./auth/LoginView";
import { Logout } from "./auth/LogoutRoute";
import { NoteDetails } from "../features/Notes";
import { ActivitiesPage } from "./accounts/ActivitiesPage";
import { AgreementDetails } from "../features/Agreements";
import { AgreementHistoryPage } from "./accounts/AgreementHistoryPage";
import { PaymentDetails } from "../features/Financials/PaymentDetails";

// The definition of a component that has been mounted by the NestedRoutes component
// it may receive the optional `routes` and `modals` props
export type PageComponent<
  Params extends { [K in keyof Params]?: string } = {}
> = React.FunctionComponent<
  Pick<RouteDefinition, "routes" | "modals"> &
    RouteComponentProps<Params> & { fallbackUrl?: string }
>;

// The definition for a ModalComponent mounted by the ModalRoutes component
// it may receive a `fallbackUrl`
export type ModalComponent<
  Params extends { [K in keyof Params]?: string } = {}
> = React.FunctionComponent<
  RouteComponentProps<Params> & { fallbackUrl?: string }
>;

export interface ModalRouteDefinition
  extends Pick<RouteDefinition, "component" | "exact" | "protected"> {
  extension: string | string[];
}

export type RouteDefinition = {
  path: string | string[];
  fallbackPath?: string;
  exact?: boolean;
  component: PageComponent<any>;
  protected?: boolean;
  routes?: RouteDefinition[];
  modals?: ModalRouteDefinition[];
};

export const findRoute = (
  path: string,
  routes?: RouteDefinition[],
): RouteDefinition | undefined => {
  if (!routes) {
    return undefined;
  }

  return routes.find((route) => {
    if (Array.isArray(route.path) && route.path.includes(path)) {
      return true;
    } else if (route.path === path) {
      return true;
    } else {
      return findRoute(path, route?.routes);
    }
  });
};

export const routes: RouteDefinition[] = [
  {
    component: OAuth2Callback,
    path: getCallbackPath(),
    exact: true,
    protected: false,
  },
  {
    component: Logout,
    path: getLogoutPath(),
    exact: true,
    protected: false,
  },
  {
    component: LoginView,
    path: "/login",
    exact: true,
    protected: false,
  },
  {
    component: ({ location }) => (
      <Redirect from="/" to={{ ...location, pathname: "/search" }} exact />
    ),
    path: "/",
    exact: true,
    protected: true,
  },
  {
    component: SearchContainer,
    path: "/search",
    protected: true,
    modals: [
      {
        component: CallVerificationDialogContainer,
        extension: "/verify/:accountNumber",
        exact: true,
      },
    ],
  },
  {
    component: AccountsPage,
    path: [
      "/accounts/:accountNumber/tickets",
      "/accounts/:accountNumber/agreement-history",
      "/accounts/:accountNumber/activities/:pageNumber(\\d+)/:pageFilter",
      "/accounts/:accountNumber/activities/:pageNumber(\\d+)",
      "/accounts/:accountNumber/activities",
      "/accounts/:accountNumber/financials/:view",
      "/accounts/:accountNumber",
    ],
    protected: true,
    modals: [
      {
        component: WrapNotesContainer,
        extension: "/notes",
        exact: true,
      },
      {
        component: MeterPointDialog,
        extension: [
          "/properties/:propertyId/meterpoints/:meterPointId/meters/:meterId",
          "/properties/:propertyId/meterpoints/:meterPointId",
        ],
      },
      {
        component: ContactDialog,
        extension: "/contacts/:contactId",
      },
      {
        component: BillDetails,
        extension: "/bills/:billNumber",
      },
      {
        component: PaymentDetails,
        extension: "/payments/:paymentId",
      },
      {
        component: CreditDetails,
        extension: "/credits/:creditNumber",
      },
      {
        component: WrapUpNotesDetails,
        extension: "/wrapupnotes/:wrapUpNoteId",
      },
      {
        component: NoteDetails,
        extension: "/notes/:noteId",
        exact: true,
      },
      {
        component: AgreementDetails,
        extension: "/agreements/:agreementId(\\d+)",
      },
    ],
    routes: [
      {
        component: ({ match }) => (
          <Redirect
            to={`/accounts/${match.params.accountNumber}/financials/transactions`}
            exact
          />
        ),
        path: "/accounts/:accountNumber/financials",
        exact: true,
      },
      {
        component: FinancialsPage,
        path: "/accounts/:accountNumber/financials/:view",
        fallbackPath: "/accounts/:accountNumber",
        protected: true,
        modals: [
          {
            component: BillDetails,
            extension: ["/:billNumber(\\d+)", "/bills/:billNumber(\\d+)"],
            exact: true,
          },
        ],
        routes: [
          {
            component: Transactions,
            path: "/accounts/:accountNumber/financials/transactions",
          },
          {
            component: Bills,
            path: "/accounts/:accountNumber/financials/bills",
          },
          {
            component: Payments,
            path: "/accounts/:accountNumber/financials/payments",
          },
        ],
      },
      {
        component: TicketsPage,
        path: "/accounts/:accountNumber/tickets",
        fallbackPath: "/accounts/:accountNumber",
        protected: true,
      },
      {
        component: AgreementHistoryPage,
        path: "/accounts/:accountNumber/agreement-history",
        protected: true,
      },
      {
        component: ActivitiesPage,
        path: [
          "/accounts/:accountNumber/activities/:pageNumber(\\d+)/:pageFilter",
          "/accounts/:accountNumber/activities/:pageNumber(\\d+)",
          "/accounts/:accountNumber/activities",
        ],
        fallbackPath: "/accounts/:accountNumber",
        protected: true,
      },
      {
        component: DashboardView,
        path: "/accounts/:accountNumber",
        fallbackPath: "/search",
        protected: true,
      },
    ],
  },
];

function modalsToFallbackMap(
  routePath: string,
  modals?: ModalRouteDefinition[],
): Record<string, string> {
  if (!modals) return {};

  return modals?.reduce((acc, modal) => {
    const exts = Array.isArray(modal.extension)
      ? modal.extension
      : [modal.extension];
    return {
      ...acc,
      ...exts.reduce(
        (paths, ext) => ({
          ...paths,
          [routePath + ext]: routePath,
        }),
        {},
      ),
    };
  }, {});
}

function routeToFallbackMap(route?: RouteDefinition): Record<string, string> {
  if (!route) return {};

  const paths = !Array.isArray(route.path) ? [route.path] : route.path;
  return paths.reduce((acc, path) => {
    return {
      ...acc,
      [path]: route.fallbackPath ?? "/",
      ...(route?.routes?.forEach(routeToFallbackMap) || {}),
      ...(modalsToFallbackMap(path, route?.modals) || {}),
    };
  }, {});
}

// TODO: Use this map instead of `useFallbackUrl` inside `useNavigator`
const FALLBACKS = routes.reduce((acc, route): Record<string, string> => {
  return {
    ...acc,
    ...routeToFallbackMap(route),
  };
}, {});

// console.log("routes", routes);
// console.log("FALLBACKS", FALLBACKS);
